import React, { useEffect } from "react";
import { useDispatch, useSelector } from 'react-redux'
import { DatePicker, DatePickerInput, Select, SelectItem, Stack, Button, DataTableSkeleton } from "@carbon/react";
import {
  DataTable as DataTableIcon,
  Map as MapIcon
} from '@carbon/icons-react';
import Typography from "../../../components/Basics/Typography";
import Card from '../../../components/Layouts/Card';
import { CardComponent, CardRow } from '../../../components/Layouts/Card.styles';
import LoadingContent from '../../../components/Animations/LoadingContent';
import { onGetAnomaliesCycleThunk } from "./CycleAnomaly.actions";
import { useDatePicker, useSelect } from "../../../components/Inputs/Inputs.hooks";
import { checkErrorRequired } from "../../../modules/errors/validations";
import { InputWrapper } from "./CycleAnomaly.styles";
import { g100 } from '@carbon/themes';
import CycleAnomalyTable from "./CycleAnomaly.table";
import CycleAnomalyMap from "./CycleAnomaly.map";
import InformationContainer from "../../../components/Basics/Information.container";


const CycleAnomaly = () => {
  const dispatch = useDispatch();
  const { data, loading, error, gpsPoints } = useSelector(state => state.CycleAnomaly);

  const getLatestAvailableDate = () => {
    const now = new Date();
    const currentHour = now.getHours();
    
    // If current time is before 9 AM, use day before yesterday
    // If current time is after 9 AM, use yesterday
    const daysToSubtract = currentHour < 9 ? 2 : 1;
    
    const latestDate = new Date(now);
    latestDate.setDate(now.getDate() - daysToSubtract);
    
    return latestDate;
  };

  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  
  const latestAvailableDate = getLatestAvailableDate();

  const { error: dateError, value: dateValue, onChange: handleDateChange } = useDatePicker({
    initialValue: [latestAvailableDate],
    errorCallbacks: [checkErrorRequired()]
  });

  const { error: typeError, value: typeValue, onChange: handleTypeChange } = useSelect({
    initialValue: 'turno_noche', // Default to day shift for auto-load
  });

  const handleLoadData = () => {
    const selectedDate = dateValue[0];
    dispatch(onGetAnomaliesCycleThunk({
      year: selectedDate.getFullYear(),
      month: selectedDate.getMonth() + 1,
      day: selectedDate.getDate(),
      shift: typeValue
    }));
  };

  // Auto-load data on component mount
  useEffect(() => {
    // Only auto-load if we have a valid shift selected
    if (typeValue) {
      handleLoadData();
    }
  }, []); // Empty dependency array means this runs once on mount

  // Button is disabled if date error exists OR if no shift is selected
  const isSearchDisabled = Boolean(dateError) || !typeValue;

  return (
    <>
      <Stack gap={3}>
        <CardComponent width="100%">
          <Card background={g100.background} width='100%' flexDirection='column' alignItems='left' gap='20px'>
            <InputWrapper>
              <DatePicker
                datePickerType="single"
                maxDate={yesterday.toISOString()}
                value={dateValue}
                dateFormat="d-m-Y"
                onChange={handleDateChange}
              >
                <DatePickerInput
                  placeholder="dd/mm/yyyy"
                  labelText="Fecha de las anomalías"
                  id="anomaly-datepicker"
                  size="md"
                />
              </DatePicker>
              <Select
                id="select-shift"
                labelText="Tipo de Turno"
                value={typeValue}
                invalid={Boolean(typeError)}
                invalidText={typeError}
                onChange={handleTypeChange}
              >
                <SelectItem value="" text="Seleccione un turno" />
                <SelectItem value="turno_dia" text="Turno día" />
                <SelectItem value="turno_noche" text="Turno noche" />
              </Select>
              <Button
                onClick={handleLoadData}
                size="md"
                disabled={isSearchDisabled}
              >
                Buscar
              </Button>
            </InputWrapper>
          </Card>

          <Card justifyContent='flex-start'>
            <DataTableIcon size={18} />
            <Typography className='cds--type-heading-compact-01'>
              CICLOS REGISTRADOS
            </Typography>
          </Card>
          
          <InformationContainer description='Los ciclos se cargan en el mapa utilizando el botón al final de las filas.' />
          
          {loading && !error && (
            <LoadingContent />
          )}
          
          {!loading && error && (
            <CardComponent width="100%">
              <Typography className='cds--type-heading-compact-01'>
                Error al buscar anomalías de ciclo. Estamos solucionando el problema
              </Typography>
            </CardComponent>
          )}
          
          {!loading && !error && data.length === 0 && (
            <Card>
              <Card background={g100.background} width='100%' flexDirection='column' alignItems='left' gap='20px'>
                <DataTableSkeleton 
                  columnCount={5} 
                  rowCount={5}
                  showHeader={true}
                  showToolbar={true}
                />
              </Card>
            </Card>
          )}
          
          {!loading && !error && data.length > 0 && (
            <Card>
              <Card background={g100.background} width='100%' flexDirection='column' alignItems='left' gap='20px'>
                <CardRow width='inherit'>
                  <Card width='100%' padding="0">
                    <CycleAnomalyTable />
                  </Card>
                </CardRow>
              </Card>
            </Card>
          )}
          <Card justifyContent='flex-start'>
            <MapIcon size={18} />
            <Typography className='cds--type-heading-compact-01'>
              CICLOS REGISTRADOS
            </Typography>
          </Card>

          <Card>
            <Card background={g100.background} width='100%' flexDirection='column' alignItems='left' gap='20px'>
              <CardRow width='inherit'>
                <Card width='100%' padding="0">
                  <CycleAnomalyMap />
                </Card>
              </CardRow>
            </Card>
          </Card>
        </CardComponent>
      </Stack>
    </>
  );
}

export default CycleAnomaly;